import { useEffect, useMemo, useRef, useState } from 'react';
import { useCountryStore } from '../../shared/country/countryStore';
import { phonePlan } from '../../shared/country/phonePlan';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { identifierInputMode, normalizeIdentifierInput, phoneHintText, validateUsername } from './authShared';

export type IdentifierMode = 'phone' | 'email' | 'username' | 'unknown';

/**
 * The identifier field's mode-detection, mask, and validity logic — shared by
 * `LoginForm` and `ForgotForm` (Task 9). Pulled out of `AuthPage` where both
 * carried an identical copy after the Step 1–4 split; `RegisterForm` does not
 * use it (Task 10 makes register phone-only, at which point this generic
 * mode-detection is no longer what that screen needs).
 *
 * `setIdentifier` is the smart setter: it re-applies the phone-mask backspace
 * fix and normalization on every call, the same way the pre-extraction
 * `handleIdentifierInput` did. Callers that also need to clear a mutation's
 * error state on edit (both consumers do, each clearing different mutations)
 * still wrap this in their own `onChange` handler — that part is deliberately
 * left per-form rather than folded in here, since it differs by consumer.
 *
 * `onChange`, when given, fires with the current value on every change
 * (typed or country-driven reformat) — including once on mount. It exists so
 * `AuthPage` can keep a cross-mode draft of the identifier: this hook's own
 * state resets on every mount (a fresh instance each time a form remounts),
 * which is exactly right for the field itself but would silently drop
 * whatever was typed the moment the player switches from Login to Register
 * or Forgot, since those are separate component instances now.
 */
export function useAuthIdentifier(
  initialIdentifier: string | (() => string) = '',
  onChange?: (value: string) => void,
  explicitMode?: 'phone' | 'email',
): {
  identifier: string;
  setIdentifier: (raw: string, modeOverride?: 'phone' | 'email') => void;
  mode: IdentifierMode;
  isPhone: boolean;
  isValid: boolean;
  hint: string;
} {
  const country = useCountryStore((state) => state.country);
  const t = useI18nStore((state) => state.t);
  const [identifier, setIdentifierRaw] = useState(initialIdentifier);

  // Latest callback, read from an effect below — avoids asking every caller
  // to memoize an inline `onChange` just to keep this hook's other effects
  // (keyed on `country`, not on the callback's identity) from re-running.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    onChangeRef.current?.(identifier);
  }, [identifier]);

  // Re-format an already-typed phone number when the country changes.
  //
  // Masks are per-country: switching Brazil → Argentina leaves '(11) 987-654-321'
  // sitting under an '+54' chip, which reads as a number that is not the one being
  // submitted. Keyed on the country code rather than the country object so a list
  // refetch that returns an equal-but-new object does not rewrite what the player
  // is mid-way through typing.
  const lastCountryCodeRef = useRef(country.code);
  useEffect(() => {
    if (lastCountryCodeRef.current === country.code) return;
    lastCountryCodeRef.current = country.code;
    setIdentifierRaw((current) => (
      (explicitMode || identifierInputMode(current)) === 'phone' ? phonePlan(country).format(current, country) : current
    ));
  }, [country, explicitMode]);

  function setIdentifier(value: string, modeOverride?: 'phone' | 'email') {
    const chosenMode = modeOverride || explicitMode;
    setIdentifierRaw((prev) => {
      if (chosenMode === 'email') return value;
      // Legacy backspace fix: deleting a phone-mask formatting char (with the
      // digit count unchanged) drops the last digit instead of re-producing
      // the same mask.
      const prevDigits = prev.replace(/\D/g, '');
      const nextDigits = value.replace(/\D/g, '');
      if (identifierInputMode(prev) === 'phone' && value.length < prev.length && nextDigits === prevDigits && prevDigits.length > 0) {
        return phonePlan(country).format(prevDigits.slice(0, -1), country);
      }
      return chosenMode === 'phone' ? phonePlan(country).format(value, country) : normalizeIdentifierInput(value, country);
    });
  }

  const mode = explicitMode || identifierInputMode(identifier);
  const isPhone = mode === 'phone';
  const hint = mode === 'phone' && identifier.trim() ? phoneHintText(identifier, country, t) : '';

  // Legacy gating: identifier must be fully valid for its detected mode.
  const isValid = useMemo(() => {
    const value = identifier.trim();
    if (!value) return false;
    if (mode === 'phone') return phonePlan(country).isValid(value, country);
    if (mode === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    if (mode === 'username') return validateUsername(value).ok;
    return false;
  }, [country, identifier, mode]);

  return { identifier, setIdentifier, mode, isPhone, isValid, hint };
}
